import { Fragment } from 'react'
import type { NotionBlock } from '@/hooks/useTalentNotion'
import styles from './NotionBlockRenderer.module.css'

interface NotionBlockRendererProps {
  blocks: NotionBlock[]
}

/** Group a run of consecutive list-item blocks of the same kind into one list. */
function isBullet(type: string): boolean {
  return type === 'bulleted_list_item'
}
function isNumbered(type: string): boolean {
  return type === 'numbered_list_item'
}

/**
 * Render Notion blocks natively (no iframe). Consecutive list items of the
 * same kind are merged into a single <ul>/<ol>; all other blocks map 1:1.
 */
export default function NotionBlockRenderer({ blocks }: NotionBlockRendererProps) {
  const out: JSX.Element[] = []
  let i = 0

  while (i < blocks.length) {
    const block = blocks[i]
    if (!block) {
      i++
      continue
    }

    // ── Merge consecutive list items ──
    if (isBullet(block.type) || isNumbered(block.type)) {
      const bullet = isBullet(block.type)
      const items: NotionBlock[] = []
      while (i < blocks.length) {
        const cur = blocks[i]
        if (!cur) break
        if (bullet ? !isBullet(cur.type) : !isNumbered(cur.type)) break
        items.push(cur)
        i++
      }
      const Tag = bullet ? 'ul' : 'ol'
      out.push(
        <Tag key={`list-${out.length}`} className={styles.list}>
          {items.map((it, j) => (
            <li key={it.id || j} className={styles.listItem}>
              {it.text}
            </li>
          ))}
        </Tag>,
      )
      continue
    }

    out.push(renderSingle(block, out.length))
    i++
  }

  return <div className={styles.viewer}>{out}</div>
}

function renderSingle(block: NotionBlock, keyIndex: number): JSX.Element {
  const key = block.id || `blk-${keyIndex}`

  switch (block.type) {
    case 'heading_1':
      return (
        <h1 key={key} className={styles.heading1}>
          {block.text}
        </h1>
      )
    case 'heading_2':
      return (
        <h2 key={key} className={styles.heading2}>
          {block.text}
        </h2>
      )
    case 'heading_3':
      return (
        <h3 key={key} className={styles.heading3}>
          {block.text}
        </h3>
      )
    case 'to_do':
      return (
        <div key={key} className={styles.todo}>
          <span className={styles.todoBox} aria-hidden="true">
            ☐
          </span>
          <span className={styles.todoText}>{block.text}</span>
        </div>
      )
    case 'code':
      return (
        <pre key={key} className={styles.codeBlock}>
          <code>{block.text}</code>
        </pre>
      )
    case 'quote':
      return (
        <blockquote key={key} className={styles.quote}>
          {block.text}
        </blockquote>
      )
    case 'callout':
      return (
        <div key={key} className={styles.callout}>
          <span className={styles.calloutIcon}>💡</span>
          <span>{block.text}</span>
        </div>
      )
    case 'divider':
      return <hr key={key} className={styles.divider} />
    case 'image':
      return block.text ? (
        <img key={key} src={block.text} alt="" className={styles.image} />
      ) : (
        <div key={key} className={styles.imagePlaceholder}>
          🖼 이미지
        </div>
      )
    case 'toggle':
      return (
        <details key={key} className={styles.toggle}>
          <summary className={styles.toggleSummary}>{block.text}</summary>
        </details>
      )
    case 'paragraph':
    default:
      return block.text ? (
        <p key={key} className={styles.paragraph}>
          {block.text}
        </p>
      ) : (
        <Fragment key={key} />
      )
  }
}
